import { Controller } from '@hotwired/stimulus';
import {
  CR_INLINE_FIELD_EVENTS,
  deleteLatestResult,
  markScopeActivity,
  observeScopeActivity,
  protocolStateFor,
  queryScope,
  scopeActivityRevision,
  scopeContextFor,
  scopeLastPointerdown,
  waitForScopeSelector,
} from './cr_inline_field_protocol';

const SAVING_MESSAGE = 'Enregistrement en cours…';
const FAILURE_MESSAGE = 'Enregistrement impossible. Réessayez.';

// Upper bound between the in-scope click on a Turbo method-link (« Marquer atteint »)
// and Turbo's requestAnimationFrame-deferred submit of the hidden form it synthesizes
// and appends to <body> (so the form is OUTSIDE the CR scope and carries no submitter).
// The protocol-recorded pointerdown is the only reliable in-scope signal; this window
// is a generous safety bound, not a tuning knob (#617 amendment C.3).
const TURBO_SUBMIT_CORRELATION_MS = 1000;

/**
 * CR Diffusion Guard Controller
 *
 * Gates the irreversible Compte rendu diffusion behind all unresolved inline
 * writes in the same explicit `meeting-report-<id>` scope.
 *
 * What survives a replacement of this guard host, and what does NOT:
 * - SURVIVES — everything owned by the shared protocol module (`protocolStateFor`):
 *   `pendingByToken`, `latestByField`, and the `guard` bag (submission intent, pending
 *   submitter, announcement, focus request). A replaced or reconnecting form therefore
 *   keeps its pending tokens, its failures, and an in-page submission.
 * - DOES NOT — `this.turboSubmissions`, an INSTANCE field re-created empty in
 *   `connect()`. An in-scope Turbo submission (a jalon « Marquer atteint » / « Annuler
 *   l'atteinte » method-link) tracked between its `turbo:submit-start` and
 *   `turbo:submit-end` would be forgotten if this host were replaced in between, and
 *   diffusion would stop waiting for it.
 *   NOT reachable today: the guarded form lives in the `cr-diffuse-modal`
 *   `Ui::ModalComponent` (`meeting_reports/show.html.erb`), a SIBLING of the document
 *   component, and the avancement/jalon write streams only replace the phase row
 *   (`cr-<report>-phase-<id>`) and the lot-header dom id.
 *
 *   ⚠️ SINCE THE LIVE DIFFUSE-RECIPIENTS FIX, ONE STREAM DOES REACH INSIDE THIS MODAL.
 *   `meeting_reports/shared/_diffuse_recipients_stream.turbo_stream.erb` replaces
 *   `AttendeesTableComponent::DIFFUSE_RECIPIENTS_DOM_ID` on every présence write (the
 *   `attendances/_section_stream` partial and `attendances/row.turbo_stream.erb`) and
 *   every carnet write in `CarnetIntervenantsController` (edit and detach, through that
 *   partial or directly for an unattached fiche). It targets the modal BODY,
 *   which is a STRICT SIBLING of `modal.with_footer`'s guarded `<form>`: the guard host
 *   is never inside that target, so `turboSubmissions` still survives every one of those
 *   writes. The invariant is therefore no longer "no stream touches this modal" but
 *   "no stream may target the guard host, the modal root, or the footer".
 *   ⚠️ NEVER widen that target to the modal or its footer, and never move the form into
 *   the document, without moving `turboSubmissions` into the protocol module in the SAME
 *   change: an in-flight jalon submission would be forgotten and the freeze would race a
 *   late POST onto a bodyless 422 (#617 amendment C.3).
 * Turbo navigation cancels submission/focus intent while preserving true PATCHes.
 *
 * Submission behavior:
 * - The first user submit is prevented and the active inline editor is blurred.
 * - Every pending token is retained until its own terminal event.
 * - In-scope Turbo form submissions (jalon « Marquer atteint »/« Annuler l'atteinte »
 *   method-links, which register NO inline-save token) are also held until their
 *   `turbo:submit-end`, so a mark → immediate diffuse cannot freeze the CR before the
 *   mark lands and leave the late POST to die on a bodyless 422 (#617 amendment C.3).
 * - Diffusion continues through one internal `requestSubmit` only when no pending
 *   token, in-flight Turbo submission, or current failure remains.
 * - A failed latest write blocks, announces the error, and focuses its replacement.
 * - Detached failures are pruned only after a bounded replacement wait confirms
 *   that no stable field selector resolves (for example after presence clears a note).
 *
 * Targets:
 * - None. A screen-reader alert region is progressively appended to the form.
 *
 * Values:
 * - None. Scope is resolved from the shared `data-cr-inline-field-scope` ancestor.
 *
 * Actions:
 * - submit: Intercepts diffusion until the report-scoped protocol is clear.
 *
 * Events consumed from `window`:
 * - cr-inline-field:pending: A token was registered, including queued saves.
 * - cr-inline-field:succeeded: One token finished successfully.
 * - cr-inline-field:failed: One token reached a failure terminal.
 *
 * Turbo cache/visit lifecycle belongs to the shared inline protocol so editable
 * non-admin CR scopes receive cleanup even when this conditional guard is absent.
 *
 * @example
 * <div data-cr-inline-field-scope="meeting-report-42">
 *   <form method="post"
 *         data-turbo="false"
 *         data-controller="cr-diffusion-guard"
 *         data-action="submit->cr-diffusion-guard#submit">
 *     <button type="submit">Figer et diffuser</button>
 *   </form>
 * </div>
 */
export default class extends Controller {
  /**
   * Reattaches listeners to persistent report state. A reconnect becomes the sole
   * active guard and immediately reevaluates any waiting submission.
   */
  connect() {
    const scope = scopeContextFor(this.element);
    this.scopeKey = scope.scopeKey;
    this.scopeElement = scope.scopeElement;
    this.protocol = protocolStateFor(this.scopeKey);
    this.protocol.guard.controller = this;
    observeScopeActivity(this.scopeKey, this.scopeElement);

    this.pendingHandler = this.#handleProtocolEvent.bind(this);
    this.succeededHandler = this.#handleProtocolEvent.bind(this);
    this.failedHandler = this.#handleProtocolEvent.bind(this);
    this.submitStartHandler = this.#handleTurboSubmitStart.bind(this);
    this.submitEndHandler = this.#handleTurboSubmitEnd.bind(this);
    this.turboSubmissions = new Set();
    this.continueTimer = null;
    this.continuationRunning = false;
    this.continuationRequested = false;
    this.lifecycleAbortController = new AbortController();

    window.addEventListener(CR_INLINE_FIELD_EVENTS.pending, this.pendingHandler);
    window.addEventListener(CR_INLINE_FIELD_EVENTS.succeeded, this.succeededHandler);
    window.addEventListener(CR_INLINE_FIELD_EVENTS.failed, this.failedHandler);
    // Turbo events bubble to `document`; the jalon method-link's synthesized form is
    // a <body> child (outside this.element), so we must listen at the document level.
    document.addEventListener('turbo:submit-start', this.submitStartHandler);
    document.addEventListener('turbo:submit-end', this.submitEndHandler);

    this.liveRegion = this.element.querySelector('[data-cr-diffusion-guard-live-region]');
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('span');
      this.liveRegion.className = 'sr-only';
      this.liveRegion.setAttribute('role', 'alert');
      this.liveRegion.setAttribute('aria-live', 'assertive');
      this.liveRegion.setAttribute('aria-atomic', 'true');
      this.liveRegion.dataset.crDiffusionGuardLiveRegion = 'true';
      this.element.append(this.liveRegion);
    }

    this.#announce(this.protocol.guard.announcement);
    this.element.setAttribute(
      'aria-busy',
      this.protocol.guard.submissionRequested ? 'true' : 'false'
    );
    this.#scheduleContinuation();
  }

  /**
   * Removes only this instance's global listeners. Shared protocol and submission
   * state deliberately survive for a reconnecting guard.
   */
  disconnect() {
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.pending, this.pendingHandler);
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.succeeded, this.succeededHandler);
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.failed, this.failedHandler);
    document.removeEventListener('turbo:submit-start', this.submitStartHandler);
    document.removeEventListener('turbo:submit-end', this.submitEndHandler);
    if (this.continueTimer) window.clearTimeout(this.continueTimer);
    this.continueTimer = null;
    this.lifecycleAbortController.abort();
    if (this.protocol.guard.controller === this) this.protocol.guard.controller = null;
  }

  /**
   * Prevents user submission, forces blur autosave, records diffusion activity,
   * and schedules one condition-based continuation. The internal one-shot submit
   * pass is allowed through unchanged.
   *
   * @param {SubmitEvent} event
   */
  submit(event) {
    const guard = this.protocol.guard;
    if (guard.nativeSubmitPass) {
      guard.nativeSubmitPass = false;
      return;
    }

    event.preventDefault();
    if (guard.submissionStarted) return;

    guard.submissionRequested = true;
    guard.pendingSubmitter = event.submitter || guard.pendingSubmitter;
    this.element.setAttribute('aria-busy', 'true');
    this.#blurActiveEditor();
    markScopeActivity(this.scopeKey);
    guard.focusRequestId += 1;
    guard.focusActivityRevision = scopeActivityRevision(this.scopeKey);
    this.#scheduleContinuation();
  }

  /**
   * Filters protocol events to this report scope and reevaluates persistent state.
   *
   * @param {CustomEvent} event
   */
  #handleProtocolEvent(event) {
    if (event.detail?.scopeKey !== this.scopeKey) return;
    this.#scheduleContinuation();
  }

  /**
   * Tracks the start of an in-scope Turbo form submission (the jalon mark/unmark
   * method-links) so diffusion is held until it settles. Keyed by the FormSubmission
   * identity when Turbo exposes it, else the submitting form node.
   *
   * @param {CustomEvent} event
   */
  #handleTurboSubmitStart(event) {
    // Le garde laisse partir SON formulaire en Turbo (« Vérifié (n) » du panneau de
    // section de la revue de l'agent, SEC-1) : dès que Turbo prend la soumission, c'est
    // lui qui tient l'envoi en cours (bouton désactivé). Sans ce relâchement,
    // `submissionStarted` resterait vrai : le microtask de `#requestNativeSubmit` ne voit
    // plus `nativeSubmitPass` (déjà consommé par `submit()`), et le « Vérifié » suivant,
    // sur la même page, serait avalé sans bruit. Le formulaire de diffusion est
    // `data-turbo="false"` : il ne passe jamais ici.
    if (event.target === this.element) this.protocol.guard.submissionStarted = false;
    if (!this.#turboSubmissionInScope(event)) return;

    this.turboSubmissions.add(this.#turboSubmissionKey(event));
    this.#scheduleContinuation();
  }

  /**
   * Clears a tracked Turbo submission and reevaluates a held diffusion. Deletion is
   * unconditional (a key never added is a no-op) so a start/end scope mismatch can
   * never leak a permanently-held entry.
   *
   * @param {CustomEvent} event
   */
  #handleTurboSubmitEnd(event) {
    this.turboSubmissions.delete(this.#turboSubmissionKey(event));
    this.#scheduleContinuation();
  }

  /**
   * True while any inline-save token OR in-scope Turbo submission is unresolved.
   *
   * @returns {boolean}
   */
  #writesInFlight() {
    return this.protocol.pendingByToken.size > 0 || this.turboSubmissions.size > 0;
  }

  /**
   * Stable key for a Turbo submission across its start/end pair.
   *
   * @param {CustomEvent} event
   * @returns {Object}
   */
  #turboSubmissionKey(event) {
    return event.detail?.formSubmission || event.target;
  }

  /**
   * Whether a Turbo submission belongs to this report scope. The jalon method-link's
   * synthesized form lives on <body> with no submitter, so DOM containment usually
   * fails. Resolution order:
   *   1. containment of the form or its submitter (ordinary in-scope forms);
   *   2. the form's `action` matching this report's avancement route prefix
   *      (`/comptes-rendus/<report_id>/avancement/`) — DETERMINISTIC and
   *      input-agnostic (covers mouse AND keyboard activation, no timing window),
   *      derived from the same `meeting-report-<id>` scope identity;
   *   3. last resort — the protocol-recorded in-scope pointerdown correlated within a
   *      short bound, retained only for a hypothetical future non-avancement in-scope
   *      method-link (mouse-only; the avancement writes never need it).
   *
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  #turboSubmissionInScope(event) {
    const form = event.target;
    if (form instanceof Element && this.scopeElement.contains(form)) return true;

    const submitter = event.detail?.formSubmission?.submitter;
    if (submitter instanceof Element && this.scopeElement.contains(submitter)) return true;

    if (this.#actionMatchesReportAvancement(this.#submissionActionUrl(event))) return true;

    const pointer = scopeLastPointerdown(this.scopeKey);
    if (!pointer || !(pointer.target instanceof Element)) return false;
    if (!this.scopeElement.contains(pointer.target)) return false;

    const sinceGesture = (event.timeStamp ?? performance.now()) - pointer.timeStamp;
    return sinceGesture >= 0 && sinceGesture <= TURBO_SUBMIT_CORRELATION_MS;
  }

  /**
   * Reads the submitting form's resolved `action` URL from the FormSubmission's form
   * element (falling back to the event target form).
   *
   * @param {CustomEvent} event
   * @returns {string|null}
   */
  #submissionActionUrl(event) {
    const form = event.detail?.formSubmission?.formElement || event.target;
    if (!(form instanceof HTMLFormElement)) return null;

    return form.action || form.getAttribute('action');
  }

  /**
   * True when an action URL targets THIS report's avancement route prefix. The report
   * id is derived from the `meeting-report-<id>` scope key, so a page-path fallback
   * scope (no explicit report identity) never matches.
   *
   * @param {string|null} actionUrl
   * @returns {boolean}
   */
  #actionMatchesReportAvancement(actionUrl) {
    if (!actionUrl) return false;

    const reportId = /^meeting-report-(\d+)$/.exec(this.scopeKey)?.[1];
    if (!reportId) return false;

    let path = actionUrl;
    try {
      path = new URL(actionUrl, window.location.origin).pathname;
    } catch {
      // Non-absolute, non-resolvable value — match against the raw string instead.
    }

    return path.includes(`/comptes-rendus/${reportId}/avancement/`);
  }

  /**
   * Defers evaluation to a new task so a prevented native submission fully unwinds
   * before an all-clear path calls `requestSubmit`.
   */
  #scheduleContinuation() {
    if (!this.#isActiveGuard()) return;

    if (this.continuationRunning) {
      this.continuationRequested = true;
      return;
    }

    if (this.continueTimer) return;

    this.continueTimer = window.setTimeout(() => {
      this.continueTimer = null;
      void this.#runContinuation();
    }, 0);
  }

  /**
   * Serializes async replacement checks within one guard instance and repeats when
   * protocol events arrive during a bounded wait.
   *
   * @returns {Promise<void>}
   */
  async #runContinuation() {
    if (!this.#isActiveGuard() || this.continuationRunning) return;

    this.continuationRunning = true;
    try {
      do {
        this.continuationRequested = false;
        await this.#continueSubmission();
      } while (this.continuationRequested && this.#isActiveGuard());
    } finally {
      this.continuationRunning = false;
    }
  }

  /**
   * Waits for every token, prunes only genuinely removed failed fields, clears a
   * recovered live error automatically, and performs one native submit on success.
   *
   * @returns {Promise<void>}
   */
  async #continueSubmission() {
    if (!this.#isActiveGuard()) return;

    const guard = this.protocol.guard;
    if (this.#writesInFlight()) {
      if (guard.submissionRequested) this.#announce(SAVING_MESSAGE);
      return;
    }

    await this.#pruneDetachedFailures();
    if (!this.#isActiveGuard()) return;
    if (this.#writesInFlight()) {
      if (guard.submissionRequested) this.#announce(SAVING_MESSAGE);
      return;
    }

    const failure = Array.from(this.protocol.latestByField.values()).find(
      ({ outcome }) => outcome === 'failed'
    );

    if (failure) {
      if (!guard.submissionRequested && guard.focusActivityRevision === null) return;

      const focusRequest = {
        id: guard.focusRequestId,
        activityRevision: guard.focusActivityRevision,
      };
      guard.submissionRequested = false;
      guard.pendingSubmitter = null;
      this.element.setAttribute('aria-busy', 'false');
      this.#resetLoadingButton();
      this.#announce(FAILURE_MESSAGE);
      await this.#focusFailure(failure, focusRequest);
      if (this.#isActiveGuard() && guard.focusRequestId === focusRequest.id) {
        guard.focusActivityRevision = null;
      }
      return;
    }

    if (guard.announcement === FAILURE_MESSAGE) this.#announce('');
    if (!guard.submissionRequested || guard.submissionStarted) return;

    guard.submissionStarted = true;
    guard.submissionRequested = false;
    guard.focusActivityRevision = null;
    this.element.setAttribute('aria-busy', 'false');
    this.#announce('');
    this.#resetLoadingButton();
    this.#requestNativeSubmit();
  }

  /**
   * Keeps failures while either their original field remains connected or the
   * stable replacement selector resolves. A bounded MutationObserver wait prevents
   * a 422 Turbo Stream from being mistaken for a deliberately removed editor.
   *
   * @returns {Promise<void>}
   */
  async #pruneDetachedFailures() {
    const failures = Array.from(this.protocol.latestByField.values()).filter(
      ({ outcome }) => outcome === 'failed'
    );

    for (const failure of failures) {
      if (failure.field?.isConnected || queryScope(this.scopeElement, failure.fieldSelector)) {
        continue;
      }

      const replacement = await waitForScopeSelector(this.scopeElement, [failure.fieldSelector], {
        signal: this.lifecycleAbortController.signal,
      });
      if (!this.#isActiveGuard()) return;
      const latest = this.protocol.latestByField.get(failure.fieldKey);
      if (latest?.token !== failure.token || latest.outcome !== 'failed') continue;
      if (failure.field?.isConnected || replacement) continue;

      deleteLatestResult(this.scopeKey, failure.fieldKey, failure.token);
    }
  }

  /**
   * Focuses a failed replacement intentionally after a diffusion attempt. The
   * preferred focus selector is followed by the stable field fallback; later user
   * activity during the wait cancels the focus move.
   *
   * @param {Object} failure
   * @param {{ id: number, activityRevision: number|null }} focusRequest
   * @returns {Promise<void>}
   */
  async #focusFailure(failure, focusRequest) {
    if (focusRequest.activityRevision === null) return;
    if (scopeActivityRevision(this.scopeKey) !== focusRequest.activityRevision) return;

    const selectors = [failure.focusSelector, failure.fieldSelector];
    const ignoredElements =
      failure.rendered && failure.field?.isConnected
        ? selectors.map((selector) => queryScope(this.scopeElement, selector)).filter(Boolean)
        : [];
    const target =
      (await waitForScopeSelector(this.scopeElement, selectors, {
        ignoredElements,
        signal: this.lifecycleAbortController.signal,
      })) || (!failure.rendered && failure.field?.isConnected ? failure.field : null);

    const latest = this.protocol.latestByField.get(failure.fieldKey);
    if (!this.#isActiveGuard()) return;
    if (this.protocol.guard.focusRequestId !== focusRequest.id) return;
    if (latest?.token !== failure.token || latest.outcome !== 'failed') return;
    if (scopeActivityRevision(this.scopeKey) !== focusRequest.activityRevision) return;
    if (!(target instanceof HTMLElement) || !target.isConnected || target.matches(':disabled'))
      return;

    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'center', inline: 'nearest' });
  }

  /**
   * Uses a one-shot marker around `requestSubmit`, preserving HTML validation and
   * the original submitter when it still belongs to the reconnected form.
   */
  #requestNativeSubmit() {
    const guard = this.protocol.guard;
    const submitter = guard.pendingSubmitter;
    guard.pendingSubmitter = null;
    guard.nativeSubmitPass = true;

    try {
      if (submitter?.form === this.element) {
        this.element.requestSubmit(submitter);
      } else {
        this.element.requestSubmit();
      }
    } catch (error) {
      guard.nativeSubmitPass = false;
      guard.submissionStarted = false;
      this.#resetLoadingButton();
      this.#announce(FAILURE_MESSAGE);
      console.error('[cr-diffusion-guard] Form submission failed:', error);
      return;
    }

    queueMicrotask(() => {
      if (!guard.nativeSubmitPass) return;

      guard.nativeSubmitPass = false;
      guard.submissionStarted = false;
    });
  }

  /**
   * Blurs only an active inline editor inside this same report scope. Its blur
   * action registers pending synchronously before diffusion activity is marked.
   */
  #blurActiveEditor() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;
    if (!active.closest('[data-controller~="cr-inline-field"]')) return;
    if (scopeContextFor(active).scopeKey !== this.scopeKey) return;

    active.blur();
  }

  /**
   * Rearms any opt-in loading submitter through the generic form contract. The
   * reset happens synchronously before `requestSubmit`, so the native submitter
   * remains eligible without relying on a timer; the resulting `submit` event
   * immediately restores its loading state.
   */
  #resetLoadingButton() {
    this.element.dispatchEvent(new CustomEvent('button-loading:reset'));
  }

  /**
   * Confirms this connected instance is still the report scope's active guard.
   *
   * @returns {boolean}
   */
  #isActiveGuard() {
    return this.element.isConnected && this.protocol.guard.controller === this;
  }

  /**
   * Updates both persistent announcement state and the current assertive live region.
   *
   * @param {string} message
   */
  #announce(message) {
    this.protocol.guard.announcement = message;
    if (this.liveRegion) this.liveRegion.textContent = message;
  }
}
